import DemoBadge from '@/components/feature/DemoBadge';
import { SAMPLE_AI_OUTPUT, SAMPLE_IMAGE_PLACEMENT } from './studioData';

type Props = {
  prepared: boolean;
  canPrepare: boolean;
  onPrepare: () => void;
};

type Row = { label: string; value: string; mono?: boolean; clamp?: boolean };

const ROWS: Row[] = [
  { label: 'Suggested headline', value: SAMPLE_AI_OUTPUT.headline },
  { label: 'Article summary', value: SAMPLE_AI_OUTPUT.summary },
  { label: 'Suggested category', value: SAMPLE_AI_OUTPUT.category },
  { label: 'Formatted article body', value: SAMPLE_AI_OUTPUT.body, clamp: true },
  { label: 'Suggested SEO title', value: SAMPLE_AI_OUTPUT.seoTitle },
  { label: 'Suggested meta description', value: SAMPLE_AI_OUTPUT.metaDescription },
  { label: 'Suggested URL slug', value: `/news/${SAMPLE_AI_OUTPUT.slug}`, mono: true },
  { label: 'Suggested image placement', value: SAMPLE_IMAGE_PLACEMENT },
];

const labelClass = 'block font-label uppercase tracking-widest text-[11px] text-foreground-600 mb-1';

export default function AiPrepPanel({ prepared, canPrepare, onPrepare }: Props) {
  return (
    <div className="bg-background-50 border border-background-200 rounded-lg p-5 flex flex-col">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="w-9 h-9 rounded-md bg-accent-100 text-accent-800 flex items-center justify-center text-lg"><i className="ri-magic-line"></i></span>
          <h3 className="font-heading font-bold text-xl text-foreground-950">Prepare with AI</h3>
        </div>
        <DemoBadge label="Sample AI output — no model connected" />
      </div>

      <p className="mt-3 text-sm text-foreground-600">
        This shows the proposed output: a headline, summary, category, formatted body, SEO fields and an image suggestion. Running it copies the sample below into the editor so you can review and edit it.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onPrepare}
          disabled={!canPrepare}
          className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 disabled:opacity-40 disabled:cursor-not-allowed text-foreground-950 font-label font-bold uppercase tracking-wider text-xs px-4 py-2.5 rounded-md whitespace-nowrap cursor-pointer transition-colors"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-sparkling-2-line"></i></span>
          {prepared ? 'Re-run Prepare with AI' : 'Prepare with AI'}
        </button>
        {!canPrepare && <span className="text-xs text-foreground-500">Add a file or paste text first.</span>}
      </div>

      {!prepared ? (
        <div className="mt-5 flex-1 rounded-md border border-dashed border-background-300 bg-background-100 p-5 flex flex-col items-center justify-center text-center min-h-[220px]">
          <span className="w-11 h-11 rounded-md bg-background-200 text-foreground-500 flex items-center justify-center text-2xl"><i className="ri-robot-2-line"></i></span>
          <p className="mt-3 text-sm text-foreground-600 max-w-xs">
            Run <strong className="font-semibold text-foreground-900">Prepare with AI</strong> to see the suggested headline, summary, body, SEO fields and image placement.
          </p>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          {ROWS.map((r) => (
            <div key={r.label} className="border-t border-background-200 pt-4 first:border-0 first:pt-0">
              <p className={labelClass}>{r.label}</p>
              <p className={`text-sm text-foreground-800 leading-relaxed whitespace-pre-line ${r.mono ? 'font-mono text-foreground-700' : ''} ${r.clamp ? 'line-clamp-6' : ''}`}>{r.value}</p>
            </div>
          ))}
        </div>
      )}

      <p className="mt-4 bg-secondary-50 border border-secondary-200 text-secondary-900 text-xs rounded-md px-3.5 py-3 flex items-start gap-2">
        <span className="w-4 h-4 flex items-center justify-center mt-0.5 shrink-0"><i className="ri-flask-line"></i></span>
        <span><strong className="font-semibold">Simulated output.</strong> No AI model is connected in this prototype — these are clearly labelled sample suggestions, not a real model response.</span>
      </p>
    </div>
  );
}